import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { BACKDROP_CLASS, useSidebarShell } from "./useSidebarShell.js";
import { SidebarNavItem } from "./SidebarNavItem.js";
import { SidebarSubjectRow } from "./SidebarSubjectRow.js";
import { SidebarChapterRow } from "./SidebarChapterRow.js";
import { IconButton } from "../ui/IconButton.js";
import { ColorSwatchPicker } from "../ui/ColorSwatchPicker.js";
import { useMapEditing } from "../maps/MapEditingContext.js";
import { NodeInspector } from "../maps/NodeInspector.js";
import { useQuery } from "../../api/useQuery.js";
import { getSubjectList, getSubjectOverview } from "../../api/queries.js";
import { LoadingSkeleton } from "../ui/LoadingPlaceholders.js";
import { ErrorState, Icon, useT } from "@mmm/ui";
const NAV_COLLAPSED_KEY = "mmm-sidebar-nav-collapsed";
const SUBJECTS_COLLAPSED_KEY = "mmm-sidebar-subjects-collapsed";

/**
 * Navigation only — subjects and chapters are created/renamed/archived/
 * deleted on their parent screens (subject cards on Home and the Subject
 * List; chapters on the subject's Overview tab), never from the sidebar.
 * Map node editing controls are shown only while a mind map is being edited
 * (see MapEditingContext).
 */
export function Sidebar() {
  const { key: navigationKey, pathname } = useLocation();
  const drawerRef = useRef<HTMLElement>(null);
  // Collapse means two different things either side of the mobile breakpoint —
  // a stored preference on a wide screen, a transient drawer on a phone. See
  // useSidebarShell for why keeping those apart matters.
  const { collapsed, asDrawer, setCollapsed, closeDrawer } = useSidebarShell(navigationKey, drawerRef);
  const [navCollapsed, setNavCollapsed] = useState(() => localStorage.getItem(NAV_COLLAPSED_KEY) === "1");
  const [savedSubjectsCollapsed, setSavedSubjectsCollapsed] = useState(() => localStorage.getItem(SUBJECTS_COLLAPSED_KEY) === "1");
  const subjectsQuery = useQuery(getSubjectList, undefined);
  const { data: subjects } = subjectsQuery;
  // The sidebar renders above the ":subjectId" route (it's a sibling of the
  // routed <Outlet>, not a descendant), so useParams() never sees the route
  // params — derive the open subject straight from the URL instead. This also
  // matches on chapter routes (/subjects/:id/chapters/...), so the subject's
  // chapter list stays expanded and the active chapter highlights.
  const subjectId = pathname.match(/^\/subjects\/([^/]+)/)?.[1];
  // Separate from `subjectId` on purpose: the subject match above is
  // deliberately loose (it also matches chapter routes) because it drives
  // which subject stays EXPANDED. Selection is a different question — only
  // the deepest node you're actually on should read as selected, so a subject
  // row is selected only when no chapter under it is.
  const openChapterId = pathname.match(/^\/subjects\/[^/]+\/chapters\/([^/]+)/)?.[1];
  // The active subject's chapter list isn't on SubjectDto (lightweight list
  // shape) — fetch its overview separately, only while a subject is open.
  const activeOverviewQuery = useQuery(getSubjectOverview, { subjectId: subjectId ?? "" }, { enabled: !!subjectId });
  const { data: activeOverview } = activeOverviewQuery;
  const { session: mapSession } = useMapEditing();
  const t = useT();
  const subjectsCollapsed = savedSubjectsCollapsed;

  useEffect(() => {
    localStorage.setItem(NAV_COLLAPSED_KEY, navCollapsed ? "1" : "0");
  }, [navCollapsed]);

  useEffect(() => {
    localStorage.setItem(SUBJECTS_COLLAPSED_KEY, savedSubjectsCollapsed ? "1" : "0");
  }, [savedSubjectsCollapsed]);

  return (
    <>
      {/* A real element, not the shadow that used to stand in for one:
          a box-shadow paints over the workspace but does not intercept a
          single tap, so everything behind the "modal" drawer stayed live. */}
      {asDrawer && (
        <button
          type="button"
          className={BACKDROP_CLASS}
          aria-label={t("nav.collapseSidebar")}
          onClick={closeDrawer}
        />
      )}
      <aside
        ref={drawerRef}
        tabIndex={asDrawer ? -1 : undefined}
        role={asDrawer ? "dialog" : undefined}
        aria-modal={asDrawer ? true : undefined}
        aria-label={asDrawer ? t("nav.navigation") : undefined}
        className={`mmm-sidebar ${collapsed ? "mmm-sidebar--collapsed" : ""} ${mapSession ? "mmm-sidebar--map-editing" : ""}`.trim()}
      >
        <div className="mmm-sidebar-header">
          <IconButton
            icon={collapsed ? "expand" : "collapse"}
            label={collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
            className="mmm-sidebar-collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
          />
        </div>

        <div className="mmm-sidebar-section mmm-sidebar-section--nav">
          <button
            type="button"
            className="mmm-sidebar-section-label mmm-sidebar-section-toggle"
            aria-expanded={!navCollapsed}
            onClick={() => setNavCollapsed((value) => !value)}
          >
            <span>{t("nav.navigation")}</span>
            <span className={`mmm-sidebar-section-chevron ${navCollapsed ? "is-collapsed" : ""}`.trim()}>
              <Icon name="chevron" strokeWidth={2} />
            </span>
          </button>

          {!navCollapsed && (
            <nav className="mmm-sidebar-nav">
              <SidebarNavItem to="/dashboard" icon="home" label={t("nav.home")} />
              <SidebarNavItem to="/subjects" icon="grid" label={t("nav.subjects")} />
              <SidebarNavItem to="/tasks" icon="doc" label={t("nav.tasks")} />
              <SidebarNavItem to="/calendar" icon="calendar" label={t("calendar.title")} />
              <SidebarNavItem to="/tests" icon="doc" label={t("nav.tests")} />
            </nav>
          )}
        </div>

        <div className="mmm-sidebar-section">
          <button
            type="button"
            className="mmm-sidebar-section-label mmm-sidebar-section-toggle"
            aria-expanded={!subjectsCollapsed}
            onClick={() => setSavedSubjectsCollapsed((value) => !value)}
          >
            <span>{t("nav.subjects")}</span>
            <span className={`mmm-sidebar-section-chevron ${subjectsCollapsed ? "is-collapsed" : ""}`.trim()}>
              <Icon name="chevron" strokeWidth={2} />
            </span>
          </button>

          {!subjectsCollapsed && (
            <div className="mmm-sidebar-subjects">
              {subjectsQuery.error && !subjectsQuery.waiting ? (
                !collapsed && (
                  <ErrorState variant="list" title={t("subjects.loadFailed")} onRetry={subjectsQuery.retry} />
                )
              ) : subjectsQuery.loading ? (
                <>
                  <div className="mmm-sidebar-subject-row" role="status" aria-label={t("subjects.loading")}>
                    <LoadingSkeleton height={8} width="8px" className="mmm-skeleton--dot" />
                    {!collapsed && <LoadingSkeleton height={14} width="70%" />}
                  </div>
                  <div className="mmm-sidebar-subject-row">
                    <LoadingSkeleton height={8} width="8px" className="mmm-skeleton--dot" />
                    {!collapsed && <LoadingSkeleton height={14} width="54%" />}
                  </div>
                  <div className="mmm-sidebar-subject-row">
                    <LoadingSkeleton height={8} width="8px" className="mmm-skeleton--dot" />
                    {!collapsed && <LoadingSkeleton height={14} width="62%" />}
                  </div>
                </>
              ) : (subjects ?? []).map((subject) => {
                const isOpen = subject.id === subjectId;
                return (
                  <div key={subject.id}>
                    <SidebarSubjectRow subject={subject} isSelected={isOpen && !openChapterId} />
                    {isOpen && !collapsed && (
                      <div className="mmm-sidebar-subject-detail">
                        {activeOverviewQuery.error && !activeOverviewQuery.waiting ? (
                          <ErrorState variant="list" title={t("chapters.loadFailed")} onRetry={activeOverviewQuery.retry} />
                        ) : (
                          activeOverview?.chapters
                            .filter((chapter) => chapter.status === "active")
                            .map((chapter) => <SidebarChapterRow key={chapter.id} chapter={chapter} />)
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Map controls — visible only while a mind map is being edited. */}
        {mapSession?.selectedNode && !collapsed && (
          <div className="mmm-sidebar-map-tools">
            <div className="mmm-sidebar-node-tools">
              <ColorSwatchPicker value={mapSession.color} onChange={mapSession.onPick} />
              <NodeInspector
                key={`${mapSession.selectedNode.id}:${mapSession.selectedNode.width}`}
                node={mapSession.selectedNode}
                onClose={mapSession.onCloseSelection}
                onPreviewSize={mapSession.onPreviewSize}
                onCommitSize={mapSession.onCommitSize}
                variant="sidebar"
              />
            </div>
          </div>
        )}

      </aside>
    </>
  );
}
